import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, input, signal, viewChildren } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

const LENGTH = 6;
const DIGITS_ONLY = /\D/g;

@Component({
  selector: 'mh-code-input',
  standalone: true,
  imports: [TranslateModule, NgClass],
  templateUrl: './code-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CodeInputComponent), multi: true }],
})
export class CodeInputComponent implements ControlValueAccessor {
  readonly hasError = input(false);

  readonly boxes = Array.from({ length: LENGTH }, (_, i) => i);
  readonly digits = signal<string[]>(Array(LENGTH).fill(''));
  readonly disabled = signal(false);

  private readonly inputs = viewChildren<ElementRef<HTMLInputElement>>('box');

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string): void {
    const clean = (value ?? '').replace(DIGITS_ONLY, '').slice(0, LENGTH);
    const next = Array.from({ length: LENGTH }, (_, i) => clean[i] ?? '');

    this.digits.set(next);
    this.syncInputs(next);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  handleInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const typed = target.value.replace(DIGITS_ONLY, '');

    if (typed.length > 1) return this.fill(typed, index);

    this.setDigit(index, typed);
    target.value = typed;
    if (typed) this.focusBox(index + 1);
  }

  handleKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits()[index]) {
      event.preventDefault();
      this.setDigit(index - 1, '');
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft') this.focusBox(index - 1);
    if (event.key === 'ArrowRight') this.focusBox(index + 1);
  }

  handlePaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') ?? '').replace(DIGITS_ONLY, '');
    if (pasted) this.fill(pasted, index);
  }

  handleBlur(): void {
    this.onTouched();
  }

  private fill(value: string, from: number): void {
    const next = [...this.digits()];
    for (let i = 0; i < value.length && from + i < LENGTH; i++) next[from + i] = value[i];

    this.digits.set(next);
    this.syncInputs(next);
    this.emit(next);
    this.focusBox(Math.min(from + value.length, LENGTH - 1));
  }

  private setDigit(index: number, value: string): void {
    if (index < 0 || index >= LENGTH) return;

    const next = [...this.digits()];
    next[index] = value;
    this.digits.set(next);
    this.syncInputs(next);
    this.emit(next);
  }

  private emit(digits: string[]): void {
    this.onChange(digits.join(''));
  }

  private syncInputs(digits: string[]): void {
    this.inputs().forEach((ref, i) => (ref.nativeElement.value = digits[i]));
  }

  private focusBox(index: number): void {
    this.inputs()[index]?.nativeElement.focus();
  }
}
